// ** Component Imports
import { Button } from '@/shared/ui'

// ** Icon Imports
import { CircleAlertIcon, Logout } from '@/shared/ui/icons'

// ** Entity Imports
import { useGetCurrentUserQuery } from '@/entities/user'

// ** Action Imports
import { useLogoutMutation } from '@/entities/session/api/sessionApi'

// ** Third-party scripts
import { shutdownTawkTo } from '@/shared/lib/tawkTo'

const MODAL_DISMISSED_KEY = 'bookCallModalDismissed'

/**
 * Centralized, app-wide overlay shown when the current user's account has
 * expired (based on `whitelist.expiresAt`). Mount this once in the shared
 * authenticated layout — it renders nothing until the account is expired,
 * then blocks the entire screen and only offers a logout action.
 */
export const AccountExpiredOverlay = () => {
	const { data: currentUser } = useGetCurrentUserQuery()
	const [logout, { isLoading: isLoggingOut }] = useLogoutMutation()

	const expiresAt = currentUser?.whitelist?.expiresAt ?? null
	const isExpired =
		expiresAt !== null && new Date(expiresAt).getTime() < Date.now()

	if (!isExpired) return null

	const handleLogout = async () => {
		sessionStorage.removeItem(MODAL_DISMISSED_KEY)

		try {
			await logout().unwrap()
			shutdownTawkTo()
		} catch (error) {
			console.error('[Logout] request failed:', error)
		}
	}

	return (
		<div
			style={{
				position: 'fixed',
				inset: 0,
				zIndex: 2000,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				padding: '2rem',
				backgroundColor: 'rgba(16, 24, 40, 0.6)',
				backdropFilter: 'blur(4px)'
			}}
		>
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					textAlign: 'center',
					gap: '1rem',
					maxWidth: '440px',
					width: '100%',
					padding: '2.5rem 2rem',
					backgroundColor: '#ffffff',
					borderRadius: '16px',
					boxShadow: '0 20px 48px rgba(16, 24, 40, 0.18)'
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: '56px',
						height: '56px',
						borderRadius: '50%',
						backgroundColor: '#fef3f2'
					}}
				>
					<CircleAlertIcon style={{ width: '28px', height: '28px' }} />
				</div>
				<h2 style={{ margin: 0, fontWeight: 600, fontSize: '20px' }}>
					Your account has expired
				</h2>
				<p
					style={{
						margin: 0,
						color: '#667085',
						fontSize: '14px',
						lineHeight: 1.6
					}}
				>
					Your access to this account has expired. Please contact your
					administrator to renew your access.
				</p>
				<Button
					label='Logout'
					variant='primary'
					isLoading={isLoggingOut}
					onClick={handleLogout}
					startIcon={<Logout />}
					style={{
						height: '40px',
						marginTop: '0.5rem',
						fontWeight: 500,
						fontSize: '14px'
					}}
				/>
			</div>
		</div>
	)
}
